import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { UserEquipment, RecordServiceRequest } from '../types/Equipment';
import { equipmentService } from '../services/equipmentService';
import { preferencesManager } from '../utils/preferencesManager';

interface ServiceDialogProps {
  open: boolean;
  equipment: UserEquipment | null;
  onClose: () => void;
  onServiceRecorded: (updatedEquipment: UserEquipment) => void;
}

const SERVICE_TYPES = [
  'Annual Service',
  'Inspection',
  'Repair',
  'Parts Replacement',
  'Emergency Service',
  'Other',
];

const ServiceDialog: React.FC<ServiceDialogProps> = ({
  open,
  equipment,
  onClose,
  onServiceRecorded,
}) => {
  const [serviceDate, setServiceDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [serviceType, setServiceType] = useState<string>('Annual Service');
  const [serviceNotes, setServiceNotes] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!equipment) return;

    const userId = preferencesManager.getUserId();
    if (!userId) {
      alert('Please set up your user account first');
      return;
    }

    try {
      setLoading(true);

      const request: RecordServiceRequest = {
        serviceDate: new Date(serviceDate).toISOString(),
        serviceType,
        serviceNotes: serviceNotes.trim() || undefined,
      };

      const updatedEquipment = await equipmentService.recordService(
        userId,
        equipment.equipmentId,
        request
      );

      onServiceRecorded(updatedEquipment);
      handleClose();
    } catch (error) {
      console.error('Failed to record service:', error);
      alert('Failed to record service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setServiceDate(new Date().toISOString().split('T')[0]);
    setServiceType('Annual Service');
    setServiceNotes('');
    onClose();
  };

  if (!equipment) return null;

  const divesSinceService = equipment.diveCount - (equipment.divesAtLastService || 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record Service - {equipment.brand || equipment.equipmentType}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Equipment Info */}
          <Box
            sx={{
              p: 2,
              backgroundColor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="textSecondary">
              <strong>Current Status:</strong>
            </Typography>
            <Typography variant="body2">
              Total Dives: <strong>{equipment.diveCount}</strong>
            </Typography>
            <Typography variant="body2">
              Dives Since Last Service: <strong>{divesSinceService}</strong>
            </Typography>
            {equipment.serviceIntervalDives && (
              <Typography variant="body2">
                Service Interval: <strong>{equipment.serviceIntervalDives} dives</strong>
              </Typography>
            )}
            {equipment.lastServiceDate && (
              <Typography variant="body2">
                Last Service: <strong>{new Date(equipment.lastServiceDate).toLocaleDateString()}</strong>
              </Typography>
            )}
          </Box>

          {/* Service Date */}
          <TextField
            label="Service Date"
            type="date"
            value={serviceDate}
            onChange={(e) => setServiceDate(e.target.value)}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
          />

          {/* Service Type */}
          <FormControl fullWidth>
            <InputLabel>Service Type</InputLabel>
            <Select
              value={serviceType}
              label="Service Type"
              onChange={(e) => setServiceType(e.target.value)}
            >
              {SERVICE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Service Notes */}
          <TextField
            label="Service Notes (Optional)"
            multiline
            rows={4}
            value={serviceNotes}
            onChange={(e) => setServiceNotes(e.target.value)}
            placeholder="Enter any details about the service performed..."
            fullWidth
          />

          {/* Info Box */}
          <Box
            sx={{
              p: 2,
              backgroundColor: 'info.light',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.main',
            }}
          >
            <Typography variant="caption" color="info.dark">
              Recording this service will reset the dive counter for this equipment. The
              current dive count ({equipment.diveCount}) will be saved as the baseline for
              future service tracking.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Recording...' : 'Record Service'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceDialog;
